import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Modal, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faCopy, faLink, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { Form } from 'react-bootstrap-5';
import Button from 'shared/button';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import Snackbar from 'shared/snackbar';

export default function CopyShareLinkModal({
  bypassTokensWithVaultType,
  enableShareLink,
  sharingCaUrlsWithVaultType,
  show,
  onClose
}) {

  const contentsToRender = [];
  let lengthOfComponents = 0;
  // we need the amount of components there will be in each component, so we need to do the checks
  // if we should render the component, before we render the component
  if (sharingCaUrlsWithVaultType.length) lengthOfComponents++;
  if (enableShareLink) lengthOfComponents++;

  if (sharingCaUrlsWithVaultType.length > 0) {
    contentsToRender.push({
      key: 'sharing-token',
      Component: function Links() {
        return (
          <SharingTokens
            sharingCaUrlsWithVaultType={sharingCaUrlsWithVaultType}
            renderTitle={lengthOfComponents > 1}
          />
        );
      }
    });
  }

  if (enableShareLink) {
    contentsToRender.push({
      key: 'bypass-token',
      Component: function Links() {
        return (
          <BypassTokens bypassTokensWithVaultType={bypassTokensWithVaultType} renderTitle={lengthOfComponents > 1}/>);
      }
    });
  }

  return (
    <Modal
      className="copy-share-link-modal"
      dialogClassName={lengthOfComponents > 1 ? 'modal-lg' : ''}
      show={show}
      onHide={onClose}
    >
      <Modal.Body className="modal-padding text-center">
        <Button.Accent className="position-absolute close-button" plain type="button" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} size="lg"/>
        </Button.Accent>
        <FontAwesomeIcon className="icon-gradient" icon={faLink} size="3x"/>
        <h3 className="mt-4 mb-5"><strong>Link to Documents</strong></h3>
        <Row className="d-flex">
          {contentsToRender.map(Content => (
            <LinkContainer key={Content.key} lengthOfComponents={lengthOfComponents}>
              <Content.Component/>
            </LinkContainer>
          ))}
        </Row>
      </Modal.Body>
    </Modal>
  );
}

function LinkContainer({ children, lengthOfComponents }) {
  if (lengthOfComponents === 1) {
    return <Col md={12}>{children}</Col>;
  }

  return (
    <Col md={6}>
      <div className="p-4 panel panel-default m-0 h-100 d-flex flex-column gap-4" style={{ borderRadius: '4px' }}>
        {children}
      </div>
    </Col>
  );
}

LinkContainer.propTypes = {
  children: PropTypes.node.isRequired,
  lengthOfComponents: PropTypes.number.isRequired
};

function SharingTokens({ sharingCaUrlsWithVaultType, renderTitle }) {
  return (
    <React.Fragment>
      {renderTitle && (
        <h4 className="m-0"><strong>Link with CA</strong></h4>
      )}
      <div style={{ lineHeight: 1.5, fontSize: renderTitle ? '16px' : '15px' }}>
        The recipient of this link will still adhere to the CA & Access settings you've configured for your documents
      </div>

      {sharingCaUrlsWithVaultType.map(({ sharingUrl, saleOrLease }) => (
        <div key={saleOrLease} style={{ marginTop: 'auto' }}>
          <div className="d-flex align-items-end">
            <div className="w-100 mr-4 text-left">
              <Form.Label htmlFor={`sharing-token-${saleOrLease}`} style={{ fontSize: '16px' }}>
                {sharingCaUrlsWithVaultType.length > 1 && (
                  <div><span className="text-capitalize">{saleOrLease}</span> link</div>
                )}
              </Form.Label>
              <Form.Control
                id={`sharing-token-${saleOrLease}`}
                readOnly
                value={sharingUrl}
              />
            </div>
            <CopyButton copyUrl={sharingUrl}/>
          </div>
        </div>
      ))}
    </React.Fragment>
  );
}

function BypassTokens({ bypassTokensWithVaultType, renderTitle }) {
  return (
    <div className="d-flex flex-column justify-content-between h-100">
      <div>
        {renderTitle && (
          <h4 className="mt-0 mb-4"><strong>Link without CA</strong></h4>
        )}
        <div style={{ lineHeight: 1.5, fontSize: '16px' }}>
          Sharing a <strong>public</strong> link gives anyone access. Sending a link will bypass a viewer to sign
          a CA or signing into Buildout. This will not create a lead.
          <div><strong>We recommend to keep this link private.</strong></div>
        </div>
      </div>

      <div>
        {bypassTokensWithVaultType.map(({ bypassTokenUrl, saleOrLease }) => (
          <div className="d-flex align-items-end mt-4" key={saleOrLease}>
            <div className="w-100 mr-4 text-left">
              <Form.Label htmlFor={`share-link-input-${saleOrLease}`} style={{ fontSize: '16px' }}>
                {bypassTokensWithVaultType.length > 1 && (
                  <div>Shareable <span className="text-capitalize">{saleOrLease}</span> link</div>
                )}
              </Form.Label>
              <Form.Control
                className="share-link-input"
                id={`share-link-input-${saleOrLease}`}
                readOnly
                value={bypassTokenUrl}
              />
            </div>
            <CopyButton copyUrl={bypassTokenUrl}/>
          </div>
        ))}
      </div>
    </div>
  );
}

function CopyButton({ copyUrl, disabled = false }) {
  const [copied, setCopied] = useState(false);
  const copiedTimeout = useRef();

  function copyLinkToClipboard() {
    navigator.clipboard.writeText(copyUrl);

    setCopied(true);
    clearTimeout(copiedTimeout.current);
    copiedTimeout.current = setTimeout(() => {
      setCopied(false);
    }, 4000);
  }

  function snackbarClosed() {
    clearTimeout(copiedTimeout.current);
    setCopied(false);
  }

  const ButtonType = copied ? Button.Success : Button.Primary;

  return (
    <React.Fragment>
      <Snackbar
        autohide={false}
        icon={faCheckCircle}
        portalElementSelector=".copy-share-link-modal"
        position="bottom"
        show={copied}
        text="Link has been copied to your clipboard"
        variant="success"
        onClose={snackbarClosed}
      />

      <ButtonType disabled={disabled} icon={copied ? faCheck : faCopy} onClick={copyLinkToClipboard}>
        {copied ? 'Copied' : 'Copy'}
      </ButtonType>
    </React.Fragment>
  );
}

CopyButton.propTypes = {
  copyUrl: PropTypes.string.isRequired,
  disabled: PropTypes.bool
};

export const bypassTokensWithVaultTypePropTypes =
  PropTypes.arrayOf(
    PropTypes.shape({
      saleOrLease: PropTypes.oneOf(['sale', 'lease']).isRequired,
      bypassTokenUrl: PropTypes.string.isRequired
    })
  );

export const sharingCaUrlsWithVaultTypePropTypes =
  PropTypes.arrayOf(
    PropTypes.shape({
      sharingUrl: PropTypes.string,
      saleOrLease: PropTypes.oneOf(['sale', 'lease'])
    })
  );

BypassTokens.propTypes = {
  bypassTokensWithVaultType: bypassTokensWithVaultTypePropTypes,
  renderTitle: PropTypes.bool.isRequired
};

SharingTokens.propTypes = {
  sharingCaUrlsWithVaultType: sharingCaUrlsWithVaultTypePropTypes,
  renderTitle: PropTypes.bool.isRequired
};

CopyShareLinkModal.propTypes = {
  bypassTokensWithVaultType: bypassTokensWithVaultTypePropTypes,
  enableShareLink: PropTypes.bool.isRequired,
  sharingCaUrlsWithVaultType: sharingCaUrlsWithVaultTypePropTypes,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
